import React, { useState, useEffect } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  MoreVertical,
  Copy,
  Mail,
  Users,
  FileText,
  Loader2,
} from 'lucide-react';
import { PaymentPlanModal } from './PaymentPlanModal';
import { paymentPlansApi } from '../../../lib/api';

interface PaymentPlan {
  id: string;
  name: string;
  type: 'single' | 'bundle' | 'subscription' | 'yearly' | 'term';
  price: number;
  currency: string;
  interval: 'one-time' | 'monthly' | 'yearly';
  additionalFees: { name: string; amount: number }[];
  freeTrial: boolean;
  trialDays: number;
  active: boolean;
  createdAt: string;
  coursesCount: number;
  studentsCount: number;
}

const mockPlans: PaymentPlan[] = [
  {
    id: '1',
    name: 'Annual Mathematics Package',
    type: 'yearly',
    price: 2745,
    currency: '$',
    interval: 'yearly',
    additionalFees: [{ name: 'Lab Fee', amount: 50 }],
    freeTrial: true,
    trialDays: 7,
    active: true,
    createdAt: '2024-01-15',
    coursesCount: 4,
    studentsCount: 45,
  },
  {
    id: '2',
    name: 'Physics Course Bundle',
    type: 'bundle',
    price: 2745,
    currency: '$',
    interval: 'one-time',
    additionalFees: [],
    freeTrial: false,
    trialDays: 0,
    active: true,
    createdAt: '2024-02-01',
    coursesCount: 3,
    studentsCount: 32,
  },
  {
    id: '3',
    name: 'Monthly Subscription',
    type: 'subscription',
    price: 199,
    currency: '$',
    interval: 'monthly',
    additionalFees: [{ name: 'Materials', amount: 25 }],
    freeTrial: true,
    trialDays: 14,
    active: false,
    createdAt: '2024-01-20',
    coursesCount: 8,
    studentsCount: 120,
  },
  {
    id: '4',
    name: 'Single Chemistry Course',
    type: 'single',
    price: 450,
    currency: '$',
    interval: 'one-time',
    additionalFees: [],
    freeTrial: false,
    trialDays: 0,
    active: true,
    createdAt: '2024-03-01',
    coursesCount: 1,
    studentsCount: 18,
  },
];

const planTypeLabels: Record<PaymentPlan['type'], string> = {
  single: 'Single Course',
  bundle: 'Bundle',
  subscription: 'Subscription',
  yearly: 'Whole Year',
  term: 'Term',
};

const planTypeColors: Record<PaymentPlan['type'], string> = {
  single: 'bg-blue-100 text-blue-700',
  bundle: 'bg-purple-100 text-purple-700',
  subscription: 'bg-green-100 text-green-700',
  yearly: 'bg-orange-100 text-orange-700',
  term: 'bg-teal-100 text-teal-700',
};

// Transform API response to frontend format
const transformApiPlan = (apiPlan: any): PaymentPlan => ({
  id: apiPlan.id,
  name: apiPlan.name,
  type: apiPlan.type,
  price: apiPlan.price,
  currency: apiPlan.currency || '$',
  interval: apiPlan.interval,
  additionalFees: apiPlan.additional_fees || [],
  freeTrial: apiPlan.free_trial || false,
  trialDays: apiPlan.trial_days || 0,
  active: apiPlan.active ?? true,
  createdAt: apiPlan.created_at,
  coursesCount: apiPlan.courses_count || 0,
  studentsCount: apiPlan.students_count || 0,
});

// Transform frontend format to API request
const transformToApiPlan = (plan: Partial<PaymentPlan>) => ({
  name: plan.name || '',
  type: plan.type || 'single' as const,
  price: plan.price || 0,
  currency: plan.currency,
  interval: plan.interval || 'one-time' as const,
  additional_fees: plan.additionalFees,
  free_trial: plan.freeTrial,
  trial_days: plan.trialDays,
  active: plan.active,
});

export function FinancePlansTab() {
  const [plans, setPlans] = useState<PaymentPlan[]>([]);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState<PaymentPlan | null>(null);
  const [contextMenuPlanId, setContextMenuPlanId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Fetch plans on mount
  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await paymentPlansApi.list();
      if (response.plans) {
        setPlans(response.plans.map(transformApiPlan));
      }
    } catch (error) {
      console.error('Failed to fetch payment plans:', error);
      // Use mock data on error
      setPlans(mockPlans);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (planId: string) => {
    const plan = plans.find(p => p.id === planId);
    if (!plan) return;

    const newActiveState = !plan.active;

    // Optimistic update
    setPlans(plans.map(p =>
      p.id === planId ? { ...p, active: newActiveState } : p
    ));

    try {
      await paymentPlansApi.update(planId, { active: newActiveState });
    } catch (error) {
      console.error('Failed to toggle plan status:', error);
      // Revert on error
      setPlans(plans.map(p =>
        p.id === planId ? { ...p, active: !newActiveState } : p
      ));
    }
  };

  const handleEditPlan = (plan: PaymentPlan) => {
    setEditingPlan(plan);
    setShowPlanModal(true);
    setContextMenuPlanId(null);
  };

  const handleDeletePlan = async (planId: string) => {
    if (!confirm('Are you sure you want to delete this payment plan?')) {
      return;
    }

    setContextMenuPlanId(null);

    try {
      await paymentPlansApi.delete(planId);
      setPlans(plans.filter(p => p.id !== planId));
    } catch (error) {
      console.error('Failed to delete plan:', error);
      alert('Failed to delete payment plan. Please try again.');
    }
  };

  const handleDuplicatePlan = async (plan: PaymentPlan) => {
    setContextMenuPlanId(null);

    try {
      setSaving(true);
      const response = await paymentPlansApi.create({
        name: `${plan.name} (Copy)`,
        type: plan.type,
        price: plan.price,
        currency: plan.currency,
        interval: plan.interval,
        additional_fees: plan.additionalFees,
        free_trial: plan.freeTrial,
        trial_days: plan.trialDays,
        active: false, // Set copied plan as inactive by default
      });

      if (response.plan) {
        setPlans([...plans, transformApiPlan(response.plan)]);
      }
    } catch (error) {
      console.error('Failed to duplicate plan:', error);
      // Fallback to local duplicate
      const newPlan: PaymentPlan = {
        ...plan,
        id: Date.now().toString(),
        name: `${plan.name} (Copy)`,
        createdAt: new Date().toISOString().split('T')[0],
        studentsCount: 0,
        active: false,
      };
      setPlans([...plans, newPlan]);
    } finally {
      setSaving(false);
    }
  };

  const handleSavePlan = async (planData: any) => {
    try {
      setSaving(true);

      if (editingPlan) {
        // Update existing plan
        const response = await paymentPlansApi.update(editingPlan.id, transformToApiPlan(planData));
        if (response.plan) {
          setPlans(plans.map(p =>
            p.id === editingPlan.id ? transformApiPlan(response.plan) : p
          ));
        } else {
          // Fallback to local update
          setPlans(plans.map(p =>
            p.id === editingPlan.id ? { ...p, ...planData } : p
          ));
        }
      } else {
        // Create new plan
        const response = await paymentPlansApi.create(transformToApiPlan(planData));
        if (response.plan) {
          setPlans([...plans, transformApiPlan(response.plan)]);
        } else {
          // Fallback to local create
          const newPlan: PaymentPlan = {
            ...planData as unknown as PaymentPlan,
            id: Date.now().toString(),
            createdAt: new Date().toISOString().split('T')[0],
            studentsCount: 0,
            coursesCount: 0,
          };
          setPlans([...plans, newPlan]);
        }
      }

      setShowPlanModal(false);
      setEditingPlan(null);
    } catch (error) {
      console.error('Failed to save plan:', error);
      alert('Failed to save payment plan. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  return (
    <div>
      {/* Plans Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {/* Create New Plan Card */}
        <button
          onClick={() => {
            setEditingPlan(null);
            setShowPlanModal(true);
          }}
          className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-300 rounded-xl hover:border-teal-500 hover:bg-teal-50 transition-colors min-h-[280px] group"
        >
          <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mb-3 group-hover:bg-teal-200">
            <Plus className="w-6 h-6 text-teal-600" />
          </div>
          <span className="text-sm font-medium text-gray-600 group-hover:text-teal-600">
            Create Payment Plan
          </span>
        </button>

        {/* Plan Cards */}
        {plans.map((plan) => (
          <div
            key={plan.id}
            className={`bg-white rounded-xl border border-gray-200 overflow-hidden transition-opacity ${
              !plan.active ? 'opacity-60' : ''
            }`}
          >
            {/* Card Header */}
            <div className="p-4 border-b border-gray-100">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-gray-900 truncate">{plan.name}</h3>
                  <span className={`inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded-full ${planTypeColors[plan.type]}`}>
                    {planTypeLabels[plan.type]}
                  </span>
                </div>
                <div className="relative">
                  <button
                    onClick={() => setContextMenuPlanId(contextMenuPlanId === plan.id ? null : plan.id)}
                    className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                  >
                    <MoreVertical className="w-4 h-4" />
                  </button>

                  {/* Context Menu */}
                  {contextMenuPlanId === plan.id && (
                    <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                      <button
                        onClick={() => handleEditPlan(plan)}
                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <Edit className="w-4 h-4" />
                        Edit Plan
                      </button>
                      <button
                        onClick={() => handleDuplicatePlan(plan)}
                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <Copy className="w-4 h-4" />
                        Duplicate
                      </button>
                      <button
                        onClick={() => setContextMenuPlanId(null)}
                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <Mail className="w-4 h-4" />
                        Send to Students
                      </button>
                      <button
                        onClick={() => setContextMenuPlanId(null)}
                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <FileText className="w-4 h-4" />
                        View Report
                      </button>
                      <hr className="my-1" />
                      <button
                        onClick={() => handleDeletePlan(plan.id)}
                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete Plan
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Additional Fees */}
              {plan.additionalFees.length > 0 && (
                <p className="text-xs text-gray-500">
                  + {plan.additionalFees.map(f => `${f.name}: ${plan.currency}${f.amount}`).join(', ')}
                </p>
              )}

              {/* Free Trial Badge */}
              {plan.freeTrial && (
                <span className="inline-block mt-2 px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full">
                  {plan.trialDays} days free trial
                </span>
              )}
            </div>

            {/* Card Body */}
            <div className="p-4">
              {/* Price */}
              <div className="text-center mb-4">
                <div className="text-3xl font-bold text-gray-900">
                  {plan.currency}{plan.price.toLocaleString()}
                </div>
                <div className="text-sm text-gray-500">
                  {plan.interval === 'one-time' ? 'One-time payment' :
                   plan.interval === 'monthly' ? 'per month' : 'per year'}
                </div>
              </div>

              {/* Stats */}
              <div className="flex items-center justify-center gap-4 text-sm text-gray-600 mb-4">
                <div className="flex items-center gap-1">
                  <FileText className="w-4 h-4" />
                  <span>{plan.coursesCount} courses</span>
                </div>
                <div className="flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  <span>{plan.studentsCount} students</span>
                </div>
              </div>

              {/* Active Toggle */}
              <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                <span className="text-sm text-gray-600">Active</span>
                <button
                  onClick={() => handleToggleActive(plan.id)}
                  className={`relative w-10 h-5 rounded-full transition-colors ${
                    plan.active ? 'bg-teal-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
                      plan.active ? 'right-0.5' : 'left-0.5'
                    }`}
                  />
                </button>
              </div>
            </div>

            {/* Card Footer */}
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
              <span className="text-xs text-gray-500">
                Created {new Date(plan.createdAt).toLocaleDateString()}
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => handleEditPlan(plan)}
                  className="p-1.5 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded"
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDeletePlan(plan.id)}
                  className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Payment Plan Modal */}
      {showPlanModal && (
        <PaymentPlanModal
          plan={editingPlan}
          onClose={() => {
            setShowPlanModal(false);
            setEditingPlan(null);
          }}
          onSave={handleSavePlan}
          saving={saving}
        />
      )}
    </div>
  );
}
